import { useEffect, useState } from 'react';
import { INACTIVITY_WARNING_MS_RESOLVED, useAuthStore } from '@/store/authStore';
import { Button, Modal } from '@/components/common';

/**
 * Opens 60s (configurable) before the inactivity timeout fires. Two actions:
 *  - "Stay signed in" resets the inactivity timer and dismisses the modal.
 *  - "Log out now" calls logout immediately.
 *
 * Builder-prompt §3 D9. The modal is mounted once inside the authenticated
 * layout (App.tsx) and listens to `inactivityWarningOpen` from the auth store.
 *
 * Note: the global trusted-activity listener in App.tsx also resets the timer
 * on pointer/keyboard activity anywhere — including inside this modal. So
 * clicking on the modal background or the close-X already extends the session
 * AND closes the modal via resetInactivityTimer. The explicit "Log out now"
 * button is the only way to log out from inside the modal.
 */
export function InactivityWarningModal() {
  const isOpen = useAuthStore((s) => s.inactivityWarningOpen);
  const resetInactivityTimer = useAuthStore((s) => s.resetInactivityTimer);
  const dismissInactivityWarning = useAuthStore((s) => s.dismissInactivityWarning);
  const logout = useAuthStore((s) => s.logout);

  const [remainingSec, setRemainingSec] = useState(
    Math.ceil(INACTIVITY_WARNING_MS_RESOLVED / 1000),
  );

  useEffect(() => {
    if (!isOpen) return;
    setRemainingSec(Math.ceil(INACTIVITY_WARNING_MS_RESOLVED / 1000));
    const tick = setInterval(() => {
      setRemainingSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tick);
  }, [isOpen]);

  const handleStay = () => {
    resetInactivityTimer();
    dismissInactivityWarning();
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Are you still there?"
      description="Your session will end soon due to inactivity."
      onClose={dismissInactivityWarning}
      footer={
        <>
          <Button variant="outline" onClick={logout}>
            Log out now
          </Button>
          <Button variant="primary" onClick={handleStay}>
            Stay signed in
          </Button>
        </>
      }
    >
      <p className="text-sm text-[#666]">
        You will be logged out in <strong>{remainingSec}</strong> second
        {remainingSec === 1 ? '' : 's'} unless you choose to stay signed in.
      </p>
    </Modal>
  );
}
